import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Divider,
} from '@chakra-ui/react';
import React, { useState, useRef } from 'react';
import CustomChakraButton from '../../../../../CustomChakraButton/CustomChakraButton';
import FormInput from '../../../../../Forms/FormInput/FormInput';
import CustomInput from '../../../../../CustomInput/CustomInput';
import { useChildrenStack } from './useChildrenStack';

interface Props {
  isOpen: boolean;
  usersIds: number[];
  handleClose: () => void;
  classes: { label: string; value: number }[];
  afterAction?: () => void;
}

export const MassEditChildren = ({
  isOpen,
  usersIds,
  handleClose,
  classes,
  afterAction
}: Props) => {
  const { childrenStack, actualUserId, setActualUserId, handleInputChange, upgradeUsers } =
    useChildrenStack({ usersIds, classes });
  const currentChild = childrenStack.find((child) => child.consumerId === actualUserId);
  const currentSaldo = currentChild?.saldo?.replace(/\s*zł/g, '') || '';
  const [saldoFocused, setSaldoFocused] = useState(false);

  const handleSubmit = (e: React.SyntheticEvent) => {
    e.preventDefault();
    upgradeUsers(afterAction);
  };

  return (
    <Modal closeOnOverlayClick={false} isOpen={isOpen} onClose={handleClose} size="4xl" isCentered>
      <form onSubmit={handleSubmit}>
        <ModalOverlay
          sx={{
            backdropFilter: 'blur(10px)',
            backgroundColor: 'rgba(0, 0, 0, 0.5)'
          }}
        />
        <ModalContent>
          <ModalHeader className="flex flex-col">
            <h2 className="font-semibold text-lg">Edytuj dane użytkowników</h2>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody className="flex w-full">
            <div className="w-60 flex flex-col gap-spacing-xs">
              {childrenStack.map((child) => (
                <div
                  key={child.consumerId}
                  className={`text-sm font-semibold rounded-md ${actualUserId === child.consumerId ? 'bg-brand-100 text-grayLight-700' : 'text-grayLight-500'}`}
                  onClick={() => setActualUserId(child.consumerId)}>
                  {child.firstName} {child.lastName}
                </div>
              ))}
            </div>
            <div className="flex flex-col gap-spacing-xl flex-1">
              <FormInput
                key={`firstName-${actualUserId}`}
                label="Imię"
                placeholder="Podaj imię"
                inputParams={{
                  value:
                    childrenStack.find((child) => child.consumerId === actualUserId)?.firstName ||
                    '',
                  name: 'firstName',
                  type: 'text',
                  required: true,
                  minLength: 3,
                  onChange: (e) => handleInputChange('firstName', e.target.value),
                  autoFocus: true
                }}
              />
              <FormInput
                label="Nazwisko"
                placeholder="Podaj nazwisko"
                inputParams={{
                  value:
                    childrenStack.find((child) => child.consumerId === actualUserId)?.lastName ||
                    '',
                  name: 'lastName',
                  type: 'text',
                  required: true,
                  minLength: 3,
                  onChange: (e) => handleInputChange('lastName', e.target.value)
                }}
              />

              <FormInput
                label="Numer identyfikatora (brelok/karta)"
                placeholder="Podaj identyfikator"
                inputParams={{
                  value:
                    childrenStack.find((child) => child.consumerId === actualUserId)?.cardNumber ||
                    '',
                  pattern: '\\d{10}',
                  title:
                    'Numer identyfikatora musi składać się z dokładnie 10 cyfr lub może być pusty.',
                  maxLength: 10,
                  required: false,
                  onWheel: (e) => (e.target as HTMLInputElement).blur(),
                  onInput: (e) => {
                    const input = e.target as HTMLInputElement;
                    // tylko cyfry i maks. 10 znaków
                    input.value = input.value.replace(/[^0-9]/g, '').slice(0, 10);
                  },
                  onChange: (e) => {
                    const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 10);
                    handleInputChange('cardNumber', value);
                  }
                }}
              />

              <CustomInput
                inputParams={{
                  step: 0.01,
                  name: 'saldo',
                  value: saldoFocused
                    ? currentSaldo
                    : currentSaldo !== ''
                      ? `${currentSaldo} zł`
                      : '',
                  onFocus: (e) => {
                    setSaldoFocused(true);
                    // opcjonalnie: zaznacz cały tekst po kliknięciu
                    setTimeout(() => e.target.select(), 0);
                  },
                  onBlur: () => {
                    setSaldoFocused(false);
                  },
                  onInput: (e) => {
                    const input = e.target as HTMLInputElement;
                    // POZWALAMY na minus, cyfry, kropki, przecinki
                    input.value = input.value.replace(/[^\d\-,.]/g, '');
                  },
                  onChange: (e) => {
                    const cleaned = e.target.value.replace(/\s*zł/g, '');
                    handleInputChange('saldo', cleaned);
                  }
                }}
                type="default"
                size="md"
                destructive={false}
                label="Saldo (zł)"
              />
            </div>
          </ModalBody>
          <Divider className="pt-spacing-2xl pb-spacing-2xl" />
          <ModalFooter className="w-full flex justify-between gap-spacing-lg">
            <CustomChakraButton
              hierarchy="secondaryColor"
              size="lg"
              iconPosition="onlyText"
              buttonProps={{
                type: 'button',
                className: 'grow',
                onClick: handleClose
              }}>
              Zamknij
            </CustomChakraButton>
            <div className="w-48">
              <CustomChakraButton
                hierarchy="primary"
                size="lg"
                iconPosition="onlyText"
                buttonProps={{
                  className: 'grow w-48',
                  type: 'submit'
                }}>
                Zapisz zmiany
              </CustomChakraButton>
            </div>
          </ModalFooter>
        </ModalContent>
      </form>
    </Modal>
  );
};
